/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ActionIcon,
  ActionIcon$inboundSchema,
  ActionIcon$Outbound,
  ActionIcon$outboundSchema,
} from "./actionicon.js";
import {
  Actions,
  Actions$inboundSchema,
  Actions$Outbound,
  Actions$outboundSchema,
} from "./actions.js";
import {
  ConfirmationDialogConfig,
  ConfirmationDialogConfig$inboundSchema,
  ConfirmationDialogConfig$Outbound,
  ConfirmationDialogConfig$outboundSchema,
} from "./confirmationdialogconfig.js";

/**
 * The variant of the state to appear on the button.
 */
export const ButtonComponentActionConfigVariant = {
  Default: "default",
  Tier2: "tier2",
  Tier3: "tier3",
  Brand: "brand",
  Destructive: "destructive",
  Inverse: "inverse",
  Link: "link",
  Transparent: "transparent",
} as const;
/**
 * The variant of the state to appear on the button.
 */
export type ButtonComponentActionConfigVariant = OpenEnum<
  typeof ButtonComponentActionConfigVariant
>;

/**
 * The config of the button
 */
export type ButtonComponentActionConfig = {
  /**
   * Actions to take
   */
  actions?: Actions | undefined;
  /**
   * The config of the confirmation dialog.
   */
  confirmationDialogConfig?: ConfirmationDialogConfig | undefined;
  /**
   * Whether the button is disabled.
   */
  disabled?: boolean | undefined;
  /**
   * The message to display when the button is disabled.
   */
  disabledMessage?: string | undefined;
  /**
   * The config of the action icon.
   */
  icon?: ActionIcon | undefined;
  /**
   * The label of the state to appear on the button.
   */
  label: string;
  /**
   * The variant of the state to appear on the button.
   */
  variant?: ButtonComponentActionConfigVariant | undefined;
};

/** @internal */
export const ButtonComponentActionConfigVariant$inboundSchema: z.ZodType<
  ButtonComponentActionConfigVariant,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(ButtonComponentActionConfigVariant);
/** @internal */
export const ButtonComponentActionConfigVariant$outboundSchema: z.ZodType<
  string,
  z.ZodTypeDef,
  ButtonComponentActionConfigVariant
> = openEnums.outboundSchema(ButtonComponentActionConfigVariant);

/** @internal */
export const ButtonComponentActionConfig$inboundSchema: z.ZodType<
  ButtonComponentActionConfig,
  z.ZodTypeDef,
  unknown
> = z.object({
  actions: types.optional(Actions$inboundSchema),
  confirmation_dialog_config: types.optional(
    ConfirmationDialogConfig$inboundSchema,
  ),
  disabled: types.boolean().default(false),
  disabled_message: types.optional(types.string()),
  icon: types.optional(ActionIcon$inboundSchema),
  label: types.string(),
  variant: ButtonComponentActionConfigVariant$inboundSchema.default("default"),
}).transform((v) => {
  return remap$(v, {
    "confirmation_dialog_config": "confirmationDialogConfig",
    "disabled_message": "disabledMessage",
  });
});
/** @internal */
export type ButtonComponentActionConfig$Outbound = {
  actions?: Actions$Outbound | undefined;
  confirmation_dialog_config?: ConfirmationDialogConfig$Outbound | undefined;
  disabled: boolean;
  disabled_message?: string | undefined;
  icon?: ActionIcon$Outbound | undefined;
  label: string;
  variant: string;
};

/** @internal */
export const ButtonComponentActionConfig$outboundSchema: z.ZodType<
  ButtonComponentActionConfig$Outbound,
  z.ZodTypeDef,
  ButtonComponentActionConfig
> = z.object({
  actions: Actions$outboundSchema.optional(),
  confirmationDialogConfig: ConfirmationDialogConfig$outboundSchema.optional(),
  disabled: z.boolean().default(false),
  disabledMessage: z.string().optional(),
  icon: ActionIcon$outboundSchema.optional(),
  label: z.string(),
  variant: ButtonComponentActionConfigVariant$outboundSchema.default("default"),
}).transform((v) => {
  return remap$(v, {
    confirmationDialogConfig: "confirmation_dialog_config",
    disabledMessage: "disabled_message",
  });
});

export function buttonComponentActionConfigToJSON(
  buttonComponentActionConfig: ButtonComponentActionConfig,
): string {
  return JSON.stringify(
    ButtonComponentActionConfig$outboundSchema.parse(
      buttonComponentActionConfig,
    ),
  );
}
export function buttonComponentActionConfigFromJSON(
  jsonString: string,
): SafeParseResult<ButtonComponentActionConfig, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ButtonComponentActionConfig$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ButtonComponentActionConfig' from JSON`,
  );
}
